// 組織設定 › IT 監控 › 監控範本（`/org/kmgr/templates`，SPEC #kinio-kms-templates、#kinio-kms-templates-impl、#kinio-kms-ui；k1-wbs M5-02）：
//   清單（每份範本的版本、管哪些項目）→ 建立 / 編輯（存檔版本 + 1，範本出新版不自動下發）→ 批次套用（選家戶 → 差異預覽 → 第 2 級確認 → 背景工作）
//   → 套用工作的進度與逐家戶結果（可中斷、失敗的可重試）。範本是組織事務：只有範本組織的 owner / org_admin。
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { useScope } from '../../../lib/scope';
import type { MyOrg } from '../../../lib/planeApi';
import { SkeletonLines } from '../../../components/Skeleton';
import { errText, NS, StateBox } from '../parts';
import { kmgrApi } from '../api';
import { WindowsEditor } from '../widgets';
import { debounceOptions, DEFAULT_BUSINESS_WINDOW, DEFAULT_MAINTENANCE_WINDOW, defaultThresholdRules, THRESHOLD_KEYS, validateThresholds } from '../model';
import { useSchedules } from '../Oncall';
import type { Severity } from '../types';
import { PREVIEW_BATCH, tplApi } from './api';
import { bodyFromHome, changeMark, chunk, groupByOrg, jobActive, jobProgress, matchKind, newProbe, newRule, overrideText, rowFate, rowId, rowLabel, validateBody, valueText, visibleRows } from './model';
import type { Overwrite, Preview, PreviewRow, Template, TemplateBody, TemplateItem, TemplateJob, TplProbe, TplRule } from './types';

const base = '/org/kmgr/templates';

export function TemplatesRoutes({ org }: { org: MyOrg }) {
  return (
    <Routes>
      <Route index element={<TemplateList org={org} />} />
      <Route path="new" element={<TemplateEditor key="new" org={org} />} />
      <Route path=":tid" element={<TemplateDetail org={org} tab="content" />} />
      <Route path=":tid/apply" element={<TemplateDetail org={org} tab="apply" />} />
      <Route path=":tid/jobs" element={<TemplateDetail org={org} tab="jobs" />} />
      <Route path=":tid/jobs/:job" element={<TemplateDetail org={org} tab="job" />} />
    </Routes>
  );
}

function stamp(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Loading() {
  const { t } = useTranslation(NS);
  return <SkeletonLines n={3} label={t('common.loading')} />;
}

function Unavailable({ error, retry }: { error: unknown; retry(): void }) {
  const { t } = useTranslation(NS);
  return (
    <StateBox icon="⊘" title={t('tpl.unavailable')} body={errText(t, error)}>
      <button className="btn" onClick={retry}>
        {t('common.retry')}
      </button>
    </StateBox>
  );
}

// ---- 清單 ----

function TemplateList({ org }: { org: MyOrg }) {
  const { t } = useTranslation(NS);
  const [archived, setArchived] = useState(false);
  const q = useQuery({ queryKey: ['kmgr', 'templates', org.org_id, archived], queryFn: () => tplApi.list(org.api, org.org_id, archived), retry: 1 });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <Unavailable error={q.error} retry={() => void q.refetch()} />;
  const list = q.data?.templates ?? [];
  return (
    <div data-testid="km-tpl-list">
      <div className="km-block-head">
        <p className="muted" style={{ margin: 0 }}>
          {t('tpl.intro')}
        </p>
        <span className="spacer" />
        <label className="km-check">
          <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
          {t('tpl.show_archived')}
        </label>
        <Link className="btn btn-primary" to={`${base}/new`}>
          {t('tpl.new')}
        </Link>
      </div>
      {list.length === 0 ? (
        <StateBox icon="○" title={t('tpl.empty_title')} body={t('tpl.empty_body')} testId="km-tpl-empty" />
      ) : (
        <table className="km-table">
          <thead>
            <tr>
              <th>{t('tpl.col_name')}</th>
              <th>{t('tpl.col_items')}</th>
              <th>{t('tpl.col_version')}</th>
              <th>{t('tpl.col_updated')}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((tp) => (
              <tr key={tp.template_id} data-template={tp.template_id}>
                <td>
                  <Link to={`${base}/${encodeURIComponent(tp.template_id)}`}>{tp.name}</Link>
                  {tp.archived && <span className="km-chip" style={{ marginLeft: 6 }}>{t('tpl.archived')}</span>}
                  {tp.description && <span className="sub">{tp.description}</span>}
                </td>
                <td>{tp.items.map((i) => t(`tpl.item_${i}`)).join('、')}</td>
                <td>v{tp.current_version}</td>
                <td>
                  {stamp(tp.updated_at)}
                  <span className="sub">{q.data?.names?.users?.[tp.updated_by] ?? ''}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted km-sub">{t('tpl.new_hosts_note')}</p>
    </div>
  );
}

// ---- 範本頁（內容｜套用｜套用紀錄） ----

function TemplateDetail({ org, tab }: { org: MyOrg; tab: 'content' | 'apply' | 'jobs' | 'job' }) {
  const { t } = useTranslation(NS);
  const { tid = '' } = useParams();
  const q = useQuery({ queryKey: ['kmgr', 'template', org.org_id, tid], queryFn: () => tplApi.get(org.api, org.org_id, tid), retry: 1 });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <Unavailable error={q.error} retry={() => void q.refetch()} />;
  const tp = q.data;
  const here = `${base}/${encodeURIComponent(tid)}`;
  return (
    <div data-testid="km-tpl-detail">
      <div className="km-block-head">
        <h2>
          {tp.name} <span className="muted">v{tp.current_version}</span>
        </h2>
        {tp.archived && <span className="km-chip">{t('tpl.archived')}</span>}
        <span className="spacer" />
        <span className="muted">{t('tpl.applied_homes', { n: tp.applied_homes ?? 0 })}</span>
      </div>
      <nav className="km-tabs" aria-label={t('tpl.tabs')}>
        <Link to={here} aria-current={tab === 'content' ? 'page' : undefined}>
          {t('tpl.tab_content')}
        </Link>
        {!tp.archived && (
          <Link to={`${here}/apply`} aria-current={tab === 'apply' ? 'page' : undefined} data-testid="km-tpl-tab-apply">
            {t('tpl.tab_apply')}
          </Link>
        )}
        <Link to={`${here}/jobs`} aria-current={tab === 'jobs' || tab === 'job' ? 'page' : undefined}>
          {t('tpl.tab_jobs')}
        </Link>
      </nav>
      {tab === 'content' && <TemplateEditor key={tp.template_id + ':' + tp.current_version} org={org} tpl={tp} />}
      {tab === 'apply' && !tp.archived && <ApplyFlow org={org} tpl={tp} />}
      {tab === 'jobs' && <JobList org={org} tpl={tp} />}
      {tab === 'job' && <JobView org={org} tpl={tp} />}
    </div>
  );
}

// ---- 編輯 ----

function Section({ item, on, onToggle, children }: { item: TemplateItem; on: boolean; onToggle(v: boolean): void; children: ReactNode }) {
  const { t } = useTranslation(NS);
  return (
    <section className="card" style={{ marginBottom: 'var(--space-3)' }} data-item={item}>
      <label className="km-check" style={{ fontWeight: 600 }}>
        <input type="checkbox" checked={on} onChange={(e) => onToggle(e.target.checked)} data-testid={`km-tpl-on-${item}`} />
        {t(`tpl.item_${item}`)}
      </label>
      <p className="muted km-sub" style={{ marginTop: 0 }}>
        {on ? t(`tpl.item_hint_${item}`) : t('tpl.item_off')}
      </p>
      {on && children}
    </section>
  );
}

function TemplateEditor({ org, tpl }: { org: MyOrg; tpl?: Template }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const nav = useNavigate();
  const schedules = useSchedules(org);
  const [name, setName] = useState(tpl?.name ?? '');
  const [desc, setDesc] = useState(tpl?.description ?? '');
  const [body, setBody] = useState<TemplateBody>(tpl?.body ?? {});
  // 〔套用到其他家戶…〕：/org/kmgr/templates/new?from_home=<家戶>——帶入那個家戶目前的設定，存檔後直接進套用（排除那個家戶）
  const [sp] = useSearchParams();
  const fromHome = tpl ? '' : (sp.get('from_home') ?? '');
  const { homes } = useScope();
  const fromHomeName = homes.find((h) => h.home_id === fromHome)?.display_name ?? fromHome;
  const snap = useQuery({
    queryKey: ['kmgr', 'tpl-from-home', org.org_id, fromHome],
    enabled: !!fromHome && schedules.isFetched,
    staleTime: Infinity,
    queryFn: async () => {
      const opt = <T,>(p: Promise<T>) => p.catch(() => undefined);
      const [thresholds, maintenance, business, notify] = await Promise.all([
        opt(kmgrApi.thresholds(org.api, fromHome)),
        opt(kmgrApi.maintenance(org.api, fromHome)),
        opt(kmgrApi.businessHours(org.api, fromHome)),
        opt(kmgrApi.notify(org.api, fromHome)),
      ]);
      return bodyFromHome({ thresholds, maintenance, business, notify }, new Set((schedules.data ?? []).map((x) => x.schedule_id)));
    },
  });
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!snap.data || seeded) return;
    setSeeded(true);
    setBody(snap.data.body);
    setName((n) => n || t('tpl.from_home_name', { home: fromHomeName }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap.data]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const ro = !!tpl?.archived;
  const set = (patch: Partial<TemplateBody>) => setBody((b) => ({ ...b, ...patch }));
  const toggle = (item: TemplateItem, on: boolean) => {
    if (!on) return setBody((b) => ({ ...b, [item]: undefined }));
    const init: Partial<TemplateBody> = {
      severity_overrides: { rules: defaultThresholdRules().map(({ key, severity, debounce_min }) => ({ key, severity, debounce_min })), disk_pct: 90, tls_days: 14, workstation_offline_days: 7 },
      maintenance: { tz: 'Asia/Taipei', windows: [{ ...DEFAULT_MAINTENANCE_WINDOW }] },
      business_hours: { tz: 'Asia/Taipei', windows: [{ ...DEFAULT_BUSINESS_WINDOW }] },
      notify: { schedule_id: schedules.data?.[0]?.schedule_id ?? '' },
      probes: [newProbe([])],
      auto_rules: [newRule([])],
      power: { poe_warn_pct: 80, poe_high_pct: 95 },
    };
    set({ [item]: init[item] });
  };
  const sev = body.severity_overrides;
  const errs = [...validateBody(body), ...(sev ? validateThresholds({ disk_pct: sev.disk_pct ?? 90, tls_days: sev.tls_days ?? 14, workstation_offline_days: sev.workstation_offline_days ?? 7 }) : [])];
  if (!name.trim()) errs.unshift('tpl.err_name');
  const dirty = !tpl || name !== tpl.name || desc !== (tpl.description ?? '') || JSON.stringify(body) !== JSON.stringify(tpl.body ?? {});
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      if (tpl) {
        const out = await tplApi.put(org.api, org.org_id, tpl.template_id, { name: name.trim(), description: desc.trim(), body, expected_version: tpl.current_version });
        await qc.invalidateQueries({ queryKey: ['kmgr', 'template', org.org_id, tpl.template_id] });
        await qc.invalidateQueries({ queryKey: ['kmgr', 'templates', org.org_id] });
        setMsg({ ok: true, text: out.current_version > tpl.current_version ? t('tpl.saved_version', { v: out.current_version }) : t('tpl.saved') });
      } else {
        const out = await tplApi.create(org.api, org.org_id, { name: name.trim(), description: desc.trim(), body });
        await qc.invalidateQueries({ queryKey: ['kmgr', 'templates', org.org_id] });
        nav(`${base}/${encodeURIComponent(out.template_id)}` + (fromHome ? `/apply?from_home=${encodeURIComponent(fromHome)}` : ''));
      }
    } catch (e) {
      setMsg({ ok: false, text: isKinioError(e) && e.status === 409 ? t('tpl.conflict') : errText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  const archive = async () => {
    if (!tpl) return;
    setBusy(true);
    try {
      await tplApi.archive(org.api, org.org_id, tpl.template_id);
      await qc.invalidateQueries({ queryKey: ['kmgr', 'templates', org.org_id] });
      nav(base);
    } catch (e) {
      setMsg({ ok: false, text: errText(t, e) });
      setBusy(false);
    }
  };
  const schedName = Object.fromEntries((schedules.data ?? []).map((s) => [s.schedule_id, s.name]));
  return (
    <div data-testid="km-tpl-editor">
      {!tpl && <h2 className="section-title">{fromHome ? t('tpl.from_home_title', { home: fromHomeName }) : t('tpl.new_title')}</h2>}
      {fromHome && (
        <div className="km-bar" role="status" data-testid="km-tpl-from-home">
          <span className="ic" aria-hidden>
            ⧉
          </span>
          <span className="txt">
            {snap.isLoading ? t('common.loading') : snap.isError ? t('tpl.from_home_failed') : t('tpl.from_home_note', { home: fromHomeName })}
            {snap.data?.skipped.includes('notify') && ' ' + t('tpl.from_home_skip_notify')}
          </span>
        </div>
      )}
      {ro && (
        <div className="km-bar unk" role="status">
          <span className="ic" aria-hidden>
            ○
          </span>
          <span className="txt">{t('tpl.archived_note')}</span>
        </div>
      )}
      <fieldset disabled={ro} style={{ border: 0, padding: 0, margin: 0 }}>
        <section className="card" style={{ marginBottom: 'var(--space-3)' }}>
          <div className="km-form">
            <label className="field">
              <span>{t('tpl.name')}</span>
              <input className="input" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} data-testid="km-tpl-name" />
            </label>
            <label className="field">
              <span>{t('tpl.description')}</span>
              <input className="input" value={desc} maxLength={500} onChange={(e) => setDesc(e.target.value)} />
            </label>
          </div>
          <p className="muted km-sub">{t('tpl.not_included')}</p>
        </section>
        <Section item="severity_overrides" on={!!sev} onToggle={(v) => toggle('severity_overrides', v)}>
          {sev && <SeverityEditor value={sev} onChange={(v) => set({ severity_overrides: v })} />}
        </Section>
        <Section item="maintenance" on={!!body.maintenance} onToggle={(v) => toggle('maintenance', v)}>
          {body.maintenance && (
            <WindowsEditor value={body.maintenance.windows} addDefault={DEFAULT_MAINTENANCE_WINDOW} label={t('tpl.item_maintenance')} onChange={(w) => set({ maintenance: { ...body.maintenance!, windows: w } })} />
          )}
        </Section>
        <Section item="business_hours" on={!!body.business_hours} onToggle={(v) => toggle('business_hours', v)}>
          {body.business_hours && <WindowsEditor value={body.business_hours.windows} label={t('tpl.item_business_hours')} onChange={(w) => set({ business_hours: { ...body.business_hours!, windows: w } })} />}
        </Section>
        <Section item="power" on={!!body.power} onToggle={(v) => toggle('power', v)}>
          {body.power && (
            <div className="toolbar wrap" data-testid="km-tpl-power">
              {(['poe_warn_pct', 'poe_high_pct'] as const).map((k) => (
                <label key={k} className="field" style={{ maxWidth: 160 }}>
                  <span>{t(k === 'poe_warn_pct' ? 'tpl.power_warn' : 'tpl.power_high')}</span>
                  <span className="toolbar" style={{ margin: 0 }}>
                    <input
                      className="input"
                      type="number"
                      min={50}
                      max={100}
                      value={body.power![k]}
                      onChange={(e) => set({ power: { ...body.power!, [k]: Number(e.target.value) } })}
                      data-testid={`km-tpl-${k}`}
                      style={{ width: '5em', textAlign: 'right' }}
                    />
                    %
                  </span>
                </label>
              ))}
            </div>
          )}
        </Section>
        <Section item="notify" on={!!body.notify} onToggle={(v) => toggle('notify', v)}>
          {body.notify && (
            <label className="field">
              <span>{t('notify.schedule')}</span>
              <select className="input" value={body.notify.schedule_id} onChange={(e) => set({ notify: { schedule_id: e.target.value } })} data-testid="km-tpl-schedule">
                <option value="">{t('tpl.pick_schedule')}</option>
                {(schedules.data ?? []).map((s) => (
                  <option key={s.schedule_id} value={s.schedule_id}>
                    {schedName[s.schedule_id]}
                  </option>
                ))}
              </select>
              <span className="hint">{t('tpl.notify_hint')}</span>
            </label>
          )}
        </Section>
        <Section item="probes" on={!!body.probes?.length} onToggle={(v) => toggle('probes', v)}>
          <ProbesEditor value={body.probes ?? []} onChange={(p) => set({ probes: p.length ? p : undefined })} />
        </Section>
        <Section item="auto_rules" on={!!body.auto_rules?.length} onToggle={(v) => toggle('auto_rules', v)}>
          <RulesEditor value={body.auto_rules ?? []} onChange={(r) => set({ auto_rules: r.length ? r : undefined })} />
        </Section>
      </fieldset>
      {errs.length > 0 && dirty && (
        <p className="error km-sub" role="alert" data-testid="km-tpl-errors">
          {[...new Set(errs)].map((k) => t(k)).join('；')}
        </p>
      )}
      {msg && (
        <p className={msg.ok ? 'notice' : 'error'} role="status" data-testid="km-tpl-msg">
          {msg.text}
        </p>
      )}
      {!ro && (
        <div className="km-dock">
          {tpl &&
            (confirmArchive ? (
              <>
                <span className="why">{t('tpl.archive_confirm')}</span>
                <button className="btn" onClick={() => setConfirmArchive(false)}>
                  {t('common.cancel')}
                </button>
                <button className="btn btn-danger" disabled={busy} onClick={() => void archive()} data-testid="km-tpl-archive-ok">
                  {t('tpl.archive')}
                </button>
              </>
            ) : (
              <button className="btn" onClick={() => setConfirmArchive(true)} data-testid="km-tpl-archive">
                {t('tpl.archive')}
              </button>
            ))}
          <span className="why">{tpl ? t('tpl.save_note') : ''}</span>
          <button className="btn btn-primary" disabled={!dirty || busy || errs.length > 0} onClick={() => void save()} data-testid="km-tpl-save">
            {busy ? t('common.saving') : tpl ? t('tpl.save') : t('tpl.create')}
          </button>
        </div>
      )}
    </div>
  );
}

function SeverityEditor({ value, onChange }: { value: NonNullable<TemplateBody['severity_overrides']>; onChange(v: NonNullable<TemplateBody['severity_overrides']>): void }) {
  const { t } = useTranslation(NS);
  const def = defaultThresholdRules();
  const rule = (key: string) => value.rules.find((r) => r.key === key) ?? def.find((r) => r.key === key)!;
  const setRule = (key: string, patch: { severity?: Severity; debounce_min?: number | null }) => {
    const cur = rule(key);
    const next = { key, severity: patch.severity ?? cur.severity, debounce_min: patch.debounce_min !== undefined ? patch.debounce_min : cur.debounce_min };
    onChange({ ...value, rules: [...value.rules.filter((r) => r.key !== key), next] });
  };
  return (
    <div className="km-rules">
      <table className="km-table">
        <thead>
          <tr>
            <th>{t('threshold.col_condition')}</th>
            <th>{t('threshold.col_severity')}</th>
            <th>{t('threshold.col_debounce')}</th>
          </tr>
        </thead>
        <tbody>
          {THRESHOLD_KEYS.map((key) => {
            const r = rule(key);
            return (
              <tr key={key} data-rule={key}>
                <td>{t(`threshold.rule_${key}`)}</td>
                <td>
                  <select className="input" value={r.severity} aria-label={t('threshold.col_severity')} onChange={(e) => setRule(key, { severity: e.target.value as Severity })}>
                    {(['P1', 'P2', 'P3'] as Severity[]).map((s) => (
                      <option key={s} value={s}>
                        {t(`sev.${s}`)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select className="input" value={r.debounce_min === null ? 'none' : String(r.debounce_min)} aria-label={t('threshold.col_debounce')} onChange={(e) => setRule(key, { debounce_min: e.target.value === 'none' ? null : Number(e.target.value) })}>
                    {debounceOptions(key, r.debounce_min).map((d) => (
                      <option key={String(d)} value={d === null ? 'none' : String(d)}>
                        {d === null ? t('threshold.no_incident') : d === 0 ? t('threshold.immediate') : t('threshold.minutes', { n: d })}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="km-form" style={{ marginTop: 12 }}>
        {(
          [
            ['disk_pct', 'threshold.disk_pct', 90],
            ['tls_days', 'threshold.tls_days', 14],
            ['workstation_offline_days', 'threshold.ws_days', 7],
          ] as const
        ).map(([k, label, d]) => (
          <label className="field" key={k}>
            <span>{t(label)}</span>
            <input className="input" inputMode="numeric" value={value[k] ?? d} onChange={(e) => onChange({ ...value, [k]: Number(e.target.value) })} />
          </label>
        ))}
      </div>
    </div>
  );
}

function ProbesEditor({ value, onChange }: { value: TplProbe[]; onChange(v: TplProbe[]): void }) {
  const { t } = useTranslation(NS);
  const set = (i: number, p: TplProbe) => onChange(value.map((x, k) => (k === i ? p : x)));
  return (
    <div data-testid="km-tpl-probes">
      {value.map((p, i) => {
        const mk = matchKind(p);
        return (
          <div key={i} className="km-window" style={{ marginBottom: 8 }} data-probe={p.key}>
            <label className="field">
              <span>{t('tpl.probe_name')}</span>
              <input className="input" value={p.name ?? ''} onChange={(e) => set(i, { ...p, name: e.target.value })} />
            </label>
            <label className="field">
              <span>{t('tpl.key')}</span>
              <input className="input" value={p.key} onChange={(e) => set(i, { ...p, key: e.target.value.trim() })} style={{ maxWidth: 140 }} />
            </label>
            <label className="field">
              <span>{t('tpl.probe_match')}</span>
              <select
                className="input"
                value={mk === 'hosts' ? `hosts:${p.match.hosts?.class ?? ''}:${p.match.hosts?.critical ? '1' : ''}` : mk}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'network_devices') set(i, { ...p, match: { network_devices: {} }, kind: p.kind === 'file' ? 'ping' : p.kind });
                  else if (v === 'collector_self') set(i, { ...p, match: { collector_self: {} } });
                  else {
                    const [, cls, crit] = v.split(':');
                    set(i, { ...p, match: { hosts: { ...(cls ? { class: cls as 'server' | 'workstation' } : {}), ...(crit ? { critical: true } : {}) } }, kind: p.kind === 'file' ? 'tcp' : p.kind });
                  }
                }}
              >
                <option value="hosts:server:">{t('tpl.match_servers')}</option>
                <option value="hosts:server:1">{t('tpl.match_critical')}</option>
                <option value="hosts:workstation:">{t('tpl.match_workstations')}</option>
                <option value="hosts::">{t('tpl.match_all_hosts')}</option>
                <option value="network_devices">{t('tpl.match_network_devices')}</option>
                <option value="collector_self">{t('tpl.match_collector_self')}</option>
              </select>
            </label>
            <label className="field">
              <span>{t('tpl.probe_kind')}</span>
              <select
                className="input"
                value={p.kind}
                onChange={(e) => {
                  const kind = e.target.value as TplProbe['kind'];
                  set(i, {
                    ...p,
                    kind,
                    port: kind === 'tcp' ? (p.port ?? 443) : kind === 'http' ? p.port : undefined,
                    http: kind === 'http' ? (p.http ?? { scheme: 'https', path: '/' }) : undefined,
                    file: kind === 'file' ? (p.file ?? { path: '' }) : undefined,
                  });
                }}
              >
                {(mk === 'collector_self' ? ['ping', 'tcp', 'http', 'file'] : ['ping', 'tcp', 'http']).map((k) => (
                  <option key={k} value={k}>
                    {t(`tpl.kind_${k}`)}
                  </option>
                ))}
              </select>
            </label>
            {(p.kind === 'tcp' || p.kind === 'http') && (
              <label className="field">
                <span>{t('tpl.port')}</span>
                <input className="input" inputMode="numeric" value={p.port ?? ''} style={{ maxWidth: 90 }} onChange={(e) => set(i, { ...p, port: Number(e.target.value) || undefined })} />
              </label>
            )}
            {p.kind === 'http' && (
              <label className="field">
                <span>{t('tpl.http_path')}</span>
                <input className="input" value={p.http?.path ?? '/'} onChange={(e) => set(i, { ...p, http: { scheme: p.http?.scheme ?? 'https', path: e.target.value } })} />
              </label>
            )}
            {p.kind === 'file' && (
              <label className="field">
                <span>{t('tpl.file_path')}</span>
                <input className="input" value={p.file?.path ?? ''} onChange={(e) => set(i, { ...p, file: { path: e.target.value } })} />
              </label>
            )}
            <label className="field">
              <span>{t('tpl.interval')}</span>
              <select className="input" value={p.interval_s ?? 60} onChange={(e) => set(i, { ...p, interval_s: Number(e.target.value) })}>
                {[30, 60, 300, 900, 3600].map((s) => (
                  <option key={s} value={s}>
                    {s < 60 ? t('tpl.seconds', { n: s }) : t('threshold.minutes', { n: s / 60 })}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="btn btn-sm" onClick={() => onChange(value.filter((_, k) => k !== i))}>
              {t('window.remove')}
            </button>
          </div>
        );
      })}
      <button
        type="button"
        className="btn btn-sm"
        onClick={() =>
          onChange([
            ...value,
            newProbe(value.map((p) => p.key)),
          ])
        }
        data-testid="km-tpl-add-probe"
      >
        {t('tpl.add_probe')}
      </button>
      <p className="muted km-sub">{t('tpl.probe_note')}</p>
    </div>
  );
}

function RulesEditor({ value, onChange }: { value: TplRule[]; onChange(v: TplRule[]): void }) {
  const { t } = useTranslation(NS);
  const set = (i: number, r: TplRule) => onChange(value.map((x, k) => (k === i ? r : x)));
  return (
    <div data-testid="km-tpl-rules">
      {value.map((r, i) => (
        <div key={i} className="km-window" style={{ marginBottom: 8 }} data-rule-key={r.key}>
          <label className="field">
            <span>{t('tpl.rule_service')}</span>
            <input className="input" value={r.target_match.service} placeholder="nginx" onChange={(e) => set(i, { ...r, target_match: { ...r.target_match, service: e.target.value } })} data-testid="km-tpl-rule-service" />
          </label>
          <label className="field">
            <span>{t('tpl.key')}</span>
            <input className="input" value={r.key} onChange={(e) => set(i, { ...r, key: e.target.value.trim() })} style={{ maxWidth: 140 }} />
          </label>
          <label className="field">
            <span>{t('tpl.rule_hosts')}</span>
            <select className="input" value={r.target_match.class ?? ''} onChange={(e) => set(i, { ...r, target_match: { ...r.target_match, class: (e.target.value || undefined) as TplRule['target_match']['class'] } })}>
              <option value="">{t('tpl.match_all_hosts')}</option>
              <option value="server">{t('tpl.match_servers')}</option>
              <option value="workstation">{t('tpl.match_workstations')}</option>
            </select>
          </label>
          <label className="field">
            <span>{t('tpl.rule_checks')}</span>
            <input className="input" inputMode="numeric" value={r.limits.checks ?? 3} style={{ maxWidth: 90 }} onChange={(e) => set(i, { ...r, limits: { checks: Number(e.target.value) || undefined } })} />
          </label>
          <button type="button" className="btn btn-sm" onClick={() => onChange(value.filter((_, k) => k !== i))}>
            {t('window.remove')}
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-sm" onClick={() => onChange([...value, newRule(value.map((r) => r.key))])}>
        {t('tpl.add_rule')}
      </button>
      <p className="muted km-sub">{t('tpl.rule_note')}</p>
    </div>
  );
}

// ---- 批次套用 ----

function ApplyFlow({ org, tpl }: { org: MyOrg; tpl: Template }) {
  const { t } = useTranslation(NS);
  const nav = useNavigate();
  const qc = useQueryClient();
  const { homes, orgs } = useScope();
  const [sp] = useSearchParams();
  const fromHome = sp.get('from_home') ?? '';
  const candidates = useMemo(() => homes.filter((h) => (h.endpoints?.api ?? org.api) === org.api && h.home_id !== fromHome), [homes, org.api, fromHome]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [previews, setPreviews] = useState<Preview[] | null>(null);
  const [overwrite, setOverwrite] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [showSame, setShowSame] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const orgName = (id: string, names?: Record<string, string>) => names?.[id] ?? orgs.find((o) => o.org_id === id)?.display_name ?? id;
  const shown = candidates.filter((h) => !filter || (h.display_name ?? h.home_id).toLowerCase().includes(filter.toLowerCase()));
  const byOrg = new Map<string, typeof candidates>();
  for (const h of shown) byOrg.set(h.org_id ?? '', [...(byOrg.get(h.org_id ?? '') ?? []), h]);
  const toggle = (id: string, on: boolean) => setPicked((s) => {
    const n = new Set(s);
    if (on) n.add(id);
    else n.delete(id);
    return n;
  });
  const runPreview = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const out: Preview[] = [];
      for (const ids of chunk([...picked], PREVIEW_BATCH)) out.push(await tplApi.preview(org.api, org.org_id, tpl.template_id, ids, tpl.current_version));
      setPreviews(out);
      setOverwrite(new Set());
    } catch (e) {
      setMsg(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const apply = async () => {
    if (!previews) return;
    setBusy(true);
    setMsg(null);
    try {
      let last = '';
      for (const p of previews) {
        if (!p.homes.length) continue;
        const ow: Overwrite[] = [];
        for (const h of p.homes) for (const r of h.rows) if (overwrite.has(`${h.home_id}|${rowId(r)}`)) ow.push({ home_id: h.home_id, item: r.item, key: r.key });
        const res = await tplApi.apply(org.api, org.org_id, tpl.template_id, { home_ids: p.homes.map((h) => h.home_id), version: p.version, preview_id: p.preview_id, overwrite: ow });
        last = res.job_id;
      }
      await qc.invalidateQueries({ queryKey: ['kmgr', 'template', org.org_id, tpl.template_id] });
      nav(last ? `${base}/${encodeURIComponent(tpl.template_id)}/jobs/${encodeURIComponent(last)}` : `${base}/${encodeURIComponent(tpl.template_id)}/jobs`);
    } catch (e) {
      setMsg(isKinioError(e) && e.status === 410 ? t('tpl.preview_expired') : errText(t, e));
      setBusy(false);
      setConfirm(false);
    }
  };
  if (!previews)
    return (
      <div data-testid="km-tpl-pick">
        <p className="muted">{t('tpl.pick_intro', { v: tpl.current_version })}</p>
        {fromHome && (
          <p className="muted km-sub" data-testid="km-tpl-pick-from-home">
            {t('tpl.pick_from_home', { home: homes.find((h) => h.home_id === fromHome)?.display_name ?? fromHome })}
          </p>
        )}
        <div className="km-block-head">
          <input className="input" placeholder={t('tpl.pick_filter')} value={filter} onChange={(e) => setFilter(e.target.value)} style={{ maxWidth: 260 }} />
          <button className="btn btn-sm" onClick={() => setPicked(new Set(shown.map((h) => h.home_id)))}>
            {t('tpl.pick_all', { n: shown.length })}
          </button>
          <button className="btn btn-sm" onClick={() => setPicked(new Set())}>
            {t('tpl.pick_none')}
          </button>
          <span className="spacer" />
          <span className="muted">{t('tpl.picked', { n: picked.size })}</span>
        </div>
        {[...byOrg.entries()].map(([o, hs]) => (
          <section key={o} className="card" style={{ marginBottom: 'var(--space-3)' }} data-org={o}>
            <h3 className="section-title" style={{ margin: 0 }}>
              {orgName(o)}
            </h3>
            {hs.map((h) => (
              <label key={h.home_id} className="km-check">
                <input type="checkbox" checked={picked.has(h.home_id)} onChange={(e) => toggle(h.home_id, e.target.checked)} data-home={h.home_id} />
                {h.display_name ?? h.home_id}
              </label>
            ))}
          </section>
        ))}
        {msg && (
          <p className="error" role="alert">
            {msg}
          </p>
        )}
        <div className="km-dock">
          <span className="why">{t('tpl.preview_note')}</span>
          <button className="btn btn-primary" disabled={!picked.size || busy} onClick={() => void runPreview()} data-testid="km-tpl-preview">
            {busy ? t('common.loading') : t('tpl.preview', { n: picked.size })}
          </button>
        </div>
      </div>
    );
  const allHomes = previews.flatMap((p) => p.homes);
  const skipped = previews.flatMap((p) => p.skipped);
  const names = { homes: Object.assign({}, ...previews.map((p) => p.names.homes)), users: Object.assign({}, ...previews.map((p) => p.names.users)), orgs: Object.assign({}, ...previews.map((p) => p.names.orgs)) };
  const pendingBy = new Map<string, number>();
  for (const p of previews) for (const x of p.pending_rules_by_org) pendingBy.set(x.org_id, (pendingBy.get(x.org_id) ?? 0) + x.count);
  const customerOrgs = new Set(allHomes.map((h) => h.org_id).filter((o) => o !== org.org_id));
  const changes = allHomes.reduce((n, h) => n + h.changes, 0);
  return (
    <div data-testid="km-tpl-diff">
      <div className="km-bar" role="status">
        <span className="ic" aria-hidden>
          ~
        </span>
        <span className="txt">{t('tpl.diff_summary', { homes: allHomes.length, changes, orgs: customerOrgs.size })}</span>
        <label className="km-check" style={{ padding: 0 }}>
          <input type="checkbox" checked={showSame} onChange={(e) => setShowSame(e.target.checked)} />
          {t('tpl.show_same')}
        </label>
      </div>
      {[...pendingBy.entries()].map(([o, n]) => (
        <div key={o} className="km-bar warn" role="status" data-testid="km-tpl-pending">
          <span className="ic" aria-hidden>
            ⚠
          </span>
          <span className="txt">{t('tpl.pending_rules', { n, org: orgName(o, names.orgs) })}</span>
        </div>
      ))}
      {groupByOrg(allHomes, names).map((g) => (
        <section key={g.org_id} className="card" style={{ marginBottom: 'var(--space-3)' }} data-org={g.org_id}>
          <h3 className="section-title" style={{ marginTop: 0 }}>
            {orgName(g.org_id, names.orgs)} <span className="muted">· {t('tpl.n_homes', { n: g.homes.length })}</span>
          </h3>
          {g.homes.map((h) => (
            <details key={h.home_id} open={g.homes.length <= 3} data-home={h.home_id}>
              <summary>
                {names.homes[h.home_id] ?? h.home_id} <span className="muted">· {t('tpl.n_changes', { n: h.changes })}</span>
              </summary>
              <table className="km-table km-tpl-rows">
                <tbody>
                  {visibleRows(h.rows, showSame).map((r) => (
                    <DiffRow key={rowId(r)} r={r} tpl={tpl} names={names} checked={overwrite.has(`${h.home_id}|${rowId(r)}`)} onCheck={(v) => setOverwrite((s) => {
                      const n = new Set(s);
                      const k = `${h.home_id}|${rowId(r)}`;
                      if (v) n.add(k);
                      else n.delete(k);
                      return n;
                    })} />
                  ))}
                </tbody>
              </table>
            </details>
          ))}
        </section>
      ))}
      {skipped.length > 0 && (
        <section className="card" style={{ marginBottom: 'var(--space-3)' }} data-testid="km-tpl-skipped">
          <h3 className="section-title" style={{ marginTop: 0 }}>
            {t('tpl.skipped_title', { n: skipped.length })}
          </h3>
          <ul className="plain-list">
            {skipped.map((s) => (
              <li key={s.home_id} data-skip={s.reason}>
                {names.homes[s.home_id] ?? candidates.find((h) => h.home_id === s.home_id)?.display_name ?? s.home_id} · {t(`tpl.reason_${s.reason}`)}
              </li>
            ))}
          </ul>
        </section>
      )}
      {msg && (
        <p className="error" role="alert">
          {msg}
        </p>
      )}
      <div className="km-dock">
        <button className="btn" onClick={() => (setPreviews(null), setConfirm(false))}>
          {t('tpl.back_pick')}
        </button>
        {confirm ? (
          <>
            <span className="why" data-testid="km-tpl-confirm-text">
              {t('tpl.apply_confirm', { n: allHomes.length, orgs: customerOrgs.size })}
            </span>
            <button className="btn" onClick={() => setConfirm(false)}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={busy} onClick={() => void apply()} data-testid="km-tpl-apply-ok">
              {busy ? t('common.saving') : t('tpl.apply_ok')}
            </button>
          </>
        ) : (
          <>
            <span className="why">{t('tpl.apply_note')}</span>
            <button className="btn btn-primary" disabled={!allHomes.length} onClick={() => setConfirm(true)} data-testid="km-tpl-apply">
              {t('tpl.apply', { n: allHomes.length })}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function DiffRow({ r, tpl, names, checked, onCheck }: { r: PreviewRow; tpl: Template; names: Preview['names']; checked: boolean; onCheck(v: boolean): void }) {
  const { t } = useTranslation(NS);
  const fate = rowFate(r, checked);
  const ctx = { users: names.users };
  return (
    <tr className={`km-tpl-${fate}`} data-row={rowId(r)} data-fate={fate}>
      <td>
        <span aria-hidden className="km-tpl-mark">
          {changeMark(r.change)}
        </span>{' '}
        {rowLabel(t, r.item, r.key, tpl.body)}
      </td>
      <td>
        {r.customer_field ? (
          <span>{t('tpl.customer_field', { v: valueText(t, r.item, r.key, r.before, ctx) })}</span>
        ) : r.skip_reason ? (
          <span>{t(`tpl.skip_${r.skip_reason}`)}</span>
        ) : (
          <span>
            {valueText(t, r.item, r.key, r.before, ctx)} → <b>{valueText(t, r.item, r.key, r.after, ctx)}</b>
          </span>
        )}
        {r.local_override && (
          <span className="sub" data-testid="km-tpl-override">
            {overrideText(t, r.local_override, names)} · {fate === 'keep' ? t('tpl.fate_keep') : t('tpl.fate_overwrite')}
          </span>
        )}
        {r.pending_rule && <span className="sub">{t('tpl.pending_rule_row')}</span>}
      </td>
      <td className="num">
        {r.local_override && r.local_override.side === 'home_org' && r.change !== 'same' && (
          <label className="km-check" style={{ padding: 0 }}>
            <input type="checkbox" checked={checked} onChange={(e) => onCheck(e.target.checked)} data-testid="km-tpl-overwrite" />
            {t('tpl.overwrite_anyway')}
          </label>
        )}
      </td>
    </tr>
  );
}

// ---- 套用紀錄 ----

function JobList({ org, tpl }: { org: MyOrg; tpl: Template }) {
  const { t } = useTranslation(NS);
  const q = useQuery({ queryKey: ['kmgr', 'tpl-jobs', org.org_id, tpl.template_id], queryFn: () => tplApi.jobs(org.api, org.org_id, tpl.template_id), refetchInterval: (qq) => ((qq.state.data ?? []).some(jobActive) ? 3000 : false) });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <Unavailable error={q.error} retry={() => void q.refetch()} />;
  const jobs = q.data ?? [];
  if (!jobs.length) return <StateBox icon="○" title={t('tpl.jobs_empty')} />;
  return (
    <table className="km-table" data-testid="km-tpl-jobs">
      <thead>
        <tr>
          <th>{t('tpl.col_started')}</th>
          <th>{t('tpl.col_version')}</th>
          <th>{t('tpl.col_state')}</th>
          <th>{t('tpl.col_result')}</th>
        </tr>
      </thead>
      <tbody>
        {jobs.map((j) => (
          <tr key={j.job_id}>
            <td>
              <Link to={`${base}/${encodeURIComponent(tpl.template_id)}/jobs/${encodeURIComponent(j.job_id)}`}>{stamp(j.created_at)}</Link>
            </td>
            <td>v{j.version}</td>
            <td>{t(`tpl.job_${j.state}`)}</td>
            <td>{t('tpl.job_counts', { applied: j.counts.applied, skipped: j.counts.skipped, failed: j.counts.failed, total: j.total })}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function JobView({ org, tpl }: { org: MyOrg; tpl: Template }) {
  const { t } = useTranslation(NS);
  const { job = '' } = useParams();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['kmgr', 'tpl-job', org.org_id, tpl.template_id, job],
    queryFn: () => tplApi.job(org.api, org.org_id, tpl.template_id, job),
    refetchInterval: (qq) => (qq.state.data && jobActive(qq.state.data) ? 2000 : false),
  });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <Unavailable error={q.error} retry={() => void q.refetch()} />;
  const j: TemplateJob = q.data;
  const { done, pct } = jobProgress(j);
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ['kmgr', 'tpl-job', org.org_id, tpl.template_id, job] });
    } catch (e) {
      setMsg(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const homeName = (id: string) => j.names?.homes?.[id] ?? id;
  const orgName = (id: string) => j.names?.orgs?.[id] ?? id;
  return (
    <div data-testid="km-tpl-job" data-state={j.state}>
      <section className="card" style={{ marginBottom: 'var(--space-3)' }}>
        <div className="km-block-head">
          <strong>{t(`tpl.job_${j.state}`)}</strong>
          {j.cancel_requested && jobActive(j) && <span className="muted">{t('tpl.cancelling')}</span>}
          <span className="spacer" />
          <span className="muted" data-testid="km-tpl-job-progress">
            {t('tpl.job_progress', { done, total: j.total })}
          </span>
        </div>
        <div className="km-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={t('tpl.job_progress', { done, total: j.total })}>
          <span style={{ width: `${pct}%` }} />
        </div>
        <p className="km-sub" data-testid="km-tpl-job-counts">
          {t('tpl.job_counts', { applied: j.counts.applied, skipped: j.counts.skipped, failed: j.counts.failed, total: j.total })}
          {!jobActive(j) && ' · ' + t('tpl.notified', { n: j.notified_orgs.length })}
        </p>
        {msg && (
          <p className="error" role="alert">
            {msg}
          </p>
        )}
        <div className="km-dock">
          {jobActive(j) && !j.cancel_requested && (
            <button className="btn" disabled={busy} onClick={() => void act(() => tplApi.cancel(org.api, org.org_id, tpl.template_id, job))} data-testid="km-tpl-cancel">
              {t('tpl.cancel')}
            </button>
          )}
          {!jobActive(j) && j.counts.failed > 0 && !tpl.archived && (
            <button className="btn btn-primary" disabled={busy} onClick={() => void act(() => tplApi.retry(org.api, org.org_id, tpl.template_id, job))} data-testid="km-tpl-retry">
              {t('tpl.retry', { n: j.counts.failed })}
            </button>
          )}
        </div>
      </section>
      <table className="km-table" data-testid="km-tpl-job-homes">
        <thead>
          <tr>
            <th>{t('tpl.col_home')}</th>
            <th>{t('tpl.col_result')}</th>
          </tr>
        </thead>
        <tbody>
          {j.homes.map((h) => {
            const kept = h.changed_items.filter((x) => x.kept).length;
            const changed = h.changed_items.filter((x) => !x.kept && !x.skip_reason && x.change !== 'same').length;
            return (
              <tr key={h.home_id} data-home={h.home_id} data-result={h.result}>
                <td>
                  {homeName(h.home_id)}
                  <span className="sub">{orgName(h.org_id)}</span>
                </td>
                <td>
                  {t(`tpl.result_${h.result}`)}
                  {h.reason && <span className="sub">{t(`tpl.reason_${h.reason.startsWith('rule_') ? 'rule' : h.reason}`, { detail: h.reason })}</span>}
                  {h.result === 'applied' && (
                    <span className="sub">
                      {t('tpl.home_result', { changed, kept })}
                      {h.pending_rules > 0 && ' · ' + t('tpl.home_pending', { n: h.pending_rules })}
                      {h.changed_since_preview && ' · ' + t('tpl.changed_since_preview')}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

